import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePartyApi } from "../api/party-api";
import type { Proposal, ProposalSummary, RespondInput } from "../api/contracts";

// The proposal hooks (C0, party realm). Read: the N3 projection (PRESENTED/DECIDED, else
// a uniform 404 → the calm "no active proposal" state). Write: the I1 response intent —
// append-only, the advisor binds; on success invalidate the proposal so the read-back
// ("you accepted on <date>") reflects the new intent.
const proposalKey = (id: string | undefined) => ["customer", "proposal", id];

/** GET /v1/customer/recommendations (F1-B) — the party's active proposals, for in-app
 *  discovery (the "Teklifiniz" nav + the Home card), so the customer finds their proposal
 *  without a deep-link/nudge. Tolerant of a bare array OR a {data} envelope. No retry — a
 *  404 (incl. F1-B not-yet-deployed) is treated as "no active proposal", not a transient. */
export function useActiveProposals() {
  const api = usePartyApi();
  return useQuery({
    queryKey: ["customer", "proposals"],
    queryFn: async () => {
      const res = await api.get<ProposalSummary[] | { data: ProposalSummary[] }>(
        "/v1/customer/recommendations",
      );
      return Array.isArray(res) ? res : (res?.data ?? []);
    },
    retry: false,
  });
}

/** GET /v1/customer/recommendations/:id — no retry (a 404 is a state, not a transient). */
export function useProposal(id: string | undefined) {
  const api = usePartyApi();
  return useQuery({
    queryKey: proposalKey(id),
    queryFn: () => api.get<Proposal>(`/v1/customer/recommendations/${id}`),
    enabled: !!id,
    retry: false,
  });
}

/** POST /v1/customer/recommendations/:id/response — the I1 intent (no auto-bind). */
export function useRespond(id: string) {
  const api = usePartyApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RespondInput) =>
      api.post(`/v1/customer/recommendations/${id}/response`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: proposalKey(id) }),
  });
}
